import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Bot,
  Workflow,
  LayoutDashboard,
  LifeBuoy,
  ArrowRight,
  Menu,
  X,
  CheckCircle2,
  Mail,
  Clock,
  Loader2,
  ClipboardList,
  Hammer,
  Sparkles,
  Receipt,
  ShieldCheck,
  UserRound,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const navLinks = [
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#how" },
  { label: "Why Us", href: "#why" },
  { label: "Contact", href: "#contact" },
];

const services = [
  {
    icon: Bot,
    title: "AI Workflow Development",
    desc: "Custom AI agents, voice automation, and workflow tools that handle the work no one wants to do. We turn repetitive tasks into systems that run themselves.",
    featured: true,
  },
  {
    icon: Workflow,
    title: "AI Integration & Consulting",
    desc: "We embed AI into the tools and processes you already use. No rip-and-replace — just smarter systems that do more with less.",
  },
  {
    icon: LayoutDashboard,
    title: "Business Tools & Custom Software",
    desc: "Custom dashboards, CRMs, and automation that fit how you actually work. Built around your process, not someone else's template.",
  },
  {
    icon: LifeBuoy,
    title: "Support & Maintenance",
    desc: "Monitoring, updates, and unlimited small fixes. We keep your AI tools running while you run your business.",
  },
];

const steps = [
  { n: "1", icon: ClipboardList, title: "Tell us the problem", desc: "Share the task or process you want to automate. We map where AI fits best." },
  { n: "2", icon: Hammer, title: "We build it", desc: "We design, build, and deploy the workflow or tool — end to end." },
  { n: "3", icon: Sparkles, title: "It runs for you", desc: "Your systems do the work. We monitor, refine, and keep them sharp." },
];

const values = [
  { icon: Receipt, title: "Clear pricing", desc: "Predictable costs. No surprise bills, ever." },
  { icon: ShieldCheck, title: "Always supported", desc: "We stick with you long after launch." },
  { icon: UserRound, title: "Real humans", desc: "Your account manager has a name and a direct line." },
  { icon: Sparkles, title: "Practical AI", desc: "We build what moves your business, not hype." },
];

const serviceOptions = [
  "AI Workflow",
  "AI Integration & Consulting",
  "Custom Software",
  "Support & Maintenance",
  "Not Sure Yet",
];

const inputClass =
  "w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all";

const emptyForm = { full_name: "", email: "", company: "", service: "", message: "" };

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav
      data-testid="ai-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-200 shadow-[0_2px_20px_rgba(15,23,42,0.04)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <a href="#hero" data-testid="ai-logo" className="text-xl font-extrabold tracking-tight text-slate-900">
          AAEN <span className="text-indigo-600">AI Services</span>
        </a>
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <a key={l.href} href={l.href} className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="ai-nav-cta"
            className="bg-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-[0_8px_20px_rgba(79,70,229,0.25)]"
          >
            Get a Quote
          </a>
        </div>
        <button className="lg:hidden text-slate-900 p-2" onClick={() => setOpen((o) => !o)} aria-label="Toggle menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-6 py-6 flex flex-col gap-4">
          {navLinks.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-base text-slate-600 hover:text-indigo-600">
              {l.label}
            </a>
          ))}
          <a href="#contact" onClick={() => setOpen(false)} className="bg-indigo-600 text-white text-center font-semibold px-5 py-3 rounded-lg">
            Get a Quote
          </a>
        </div>
      )}
    </nav>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-sm text-slate-700 mb-2">
        {label} {required && <span className="text-indigo-600">*</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-500" data-testid="ai-field-error">{error}</p>}
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState("");

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.service) e.service = "Please choose a service.";
    if (!form.message.trim()) e.message = "Tell us a little about what you need.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setApiError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm(emptyForm);
    } catch (err) {
      setApiError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="ai-contact-success" className="flex items-start gap-4 bg-indigo-50 border border-indigo-200 rounded-2xl p-8">
        <CheckCircle2 className="text-indigo-600 shrink-0" size={28} />
        <div>
          <h3 className="font-semibold text-xl text-slate-900">Thanks — we will be in touch shortly.</h3>
          <button data-testid="ai-send-another" onClick={() => setSubmitted(false)} className="mt-4 text-indigo-600 text-sm font-semibold hover:text-indigo-700">
            Send another inquiry →
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="ai-contact-form" className="space-y-5" noValidate>
      <Field label="Full Name" required error={errors.full_name}>
        <input data-testid="ai-name" type="text" value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} placeholder="Jane Doe" className={inputClass} />
      </Field>
      <Field label="Email" required error={errors.email}>
        <input data-testid="ai-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="jane@company.com" className={inputClass} />
      </Field>
      <Field label="Company / Website" error={errors.company}>
        <input data-testid="ai-company" type="text" value={form.company} onChange={(e) => setField("company", e.target.value)} placeholder="company.com (optional)" className={inputClass} />
      </Field>
      <Field label="What service interests you?" required error={errors.service}>
        <select data-testid="ai-service" value={form.service} onChange={(e) => setField("service", e.target.value)} className={`${inputClass} appearance-none`}>
          <option value="" disabled>Select a service</option>
          {serviceOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
        </select>
      </Field>
      <Field label="Briefly describe what you need" required error={errors.message}>
        <textarea data-testid="ai-message" rows={4} value={form.message} onChange={(e) => setField("message", e.target.value)} placeholder="A few sentences about your project..." className={`${inputClass} resize-none`} />
      </Field>
      {apiError && <p className="text-sm text-red-500" role="alert" data-testid="ai-api-error">{apiError}</p>}
      <button type="submit" data-testid="ai-submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60 shadow-[0_12px_30px_rgba(79,70,229,0.3)]">
        {submitting && <Loader2 size={18} className="animate-spin" />}
        Send Inquiry
      </button>
    </form>
  );
}

export default function AIServicesPage() {
  return (
    <div className="min-h-screen bg-white text-slate-700" style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}>
      <Navbar />

      {/* HERO */}
      <section id="hero" data-testid="ai-hero" className="relative pt-40 pb-28 md:pt-48 md:pb-36 overflow-hidden">
        <div className="absolute -top-40 -left-20 w-[600px] h-[600px] rounded-full bg-indigo-200/40 blur-[130px] pointer-events-none" />
        <div className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full bg-violet-200/40 blur-[130px] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <motion.a
            href="https://aaen.ai"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-medium mb-8 hover:bg-indigo-100 transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-600" />
            A company of AAEN Holdings
          </motion.a>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-slate-900"
          >
            AI that does the work{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-500 bg-clip-text text-transparent">
              your business actually needs.
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-7 text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed"
          >
            Custom AI workflows, integrations, and tools — designed, built, and supported end to end.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a href="#contact" data-testid="ai-hero-cta" className="group inline-flex items-center gap-2 bg-indigo-600 text-white font-semibold px-7 py-3.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-[0_12px_30px_rgba(79,70,229,0.3)]">
              Get a Quote
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a href="#how" className="inline-flex items-center gap-2 bg-white text-slate-800 border border-slate-300 font-semibold px-7 py-3.5 rounded-lg hover:border-indigo-400 hover:text-indigo-600 transition-all">
              How It Works
            </a>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" data-testid="ai-services" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="text-indigo-600 text-sm font-semibold tracking-wider uppercase mb-3">What we do</p>
            <h2 className="font-bold text-3xl md:text-4xl tracking-tight text-slate-900 max-w-2xl">
              Practical AI, built around your business.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 0.07}>
                  <div data-testid={`ai-service-${i}`} className={`group h-full bg-white border rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 ${s.featured ? "border-indigo-200 shadow-[0_20px_50px_rgba(79,70,229,0.12)]" : "border-slate-200 shadow-sm hover:shadow-lg hover:border-indigo-200"}`}>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${s.featured ? "bg-indigo-600" : "bg-indigo-50 group-hover:bg-indigo-100"}`}>
                      <Icon className={s.featured ? "text-white" : "text-indigo-600"} size={24} />
                    </div>
                    <h3 className="font-semibold text-xl text-slate-900 mb-3">{s.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" data-testid="ai-how" className="py-24 md:py-32 bg-[#f6f8fc] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="text-indigo-600 text-sm font-semibold tracking-wider uppercase mb-3">How it works</p>
            <h2 className="font-bold text-3xl md:text-4xl tracking-tight text-slate-900">Three steps to working AI.</h2>
          </Reveal>
          <div className="mt-16 relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
            <div className="hidden md:block absolute top-8 left-[16%] right-[16%] border-t-2 border-dashed border-indigo-200" />
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.n} delay={i * 0.12} className="relative text-center">
                  <div className="relative mx-auto w-16 h-16 rounded-full bg-indigo-600 flex items-center justify-center shadow-[0_12px_30px_rgba(79,70,229,0.35)]">
                    <span className="font-bold text-xl text-white">{s.n}</span>
                  </div>
                  <div className="mt-6 flex justify-center"><Icon className="text-indigo-500" size={22} /></div>
                  <h3 className="font-semibold text-xl text-slate-900 mt-4 mb-3">{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed max-w-xs mx-auto">{s.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="why" data-testid="ai-why" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="text-indigo-600 text-sm font-semibold tracking-wider uppercase mb-3">Why us</p>
            <h2 className="font-bold text-3xl md:text-4xl tracking-tight text-slate-900 max-w-2xl">
              A partner, not a vendor.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 0.07}>
                  <div data-testid={`ai-why-${i}`} className="flex items-start gap-5 bg-white border border-slate-200 rounded-2xl p-7 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all duration-300">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-indigo-50 flex items-center justify-center">
                      <Icon className="text-indigo-600" size={22} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900 mb-1.5">{v.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{v.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" data-testid="ai-contact" className="py-24 md:py-32 bg-[#f6f8fc] border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <Reveal>
              <p className="text-indigo-600 text-sm font-semibold tracking-wider uppercase mb-3">Contact</p>
              <h2 className="font-bold text-3xl md:text-4xl tracking-tight text-slate-900">Let's talk about your project.</h2>
              <p className="text-slate-600 mt-4">Tell us what you need. We will get back within one business day.</p>
              <div className="mt-8"><ContactForm /></div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="lg:mt-24 bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="font-semibold text-xl text-slate-900 mb-6">Prefer email?</h3>
                <a href="mailto:hello@aaen.ai" data-testid="ai-email-link" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                    <Mail className="text-indigo-600" size={22} />
                  </div>
                  <div>
                    <p className="text-slate-900 font-medium group-hover:text-indigo-600 transition-colors">hello@aaen.ai</p>
                    <p className="text-slate-500 text-sm">Drop us a line anytime.</p>
                  </div>
                </a>
                <div className="mt-8 pt-8 border-t border-slate-200 flex items-center gap-3 text-slate-600 text-sm">
                  <Clock size={18} className="text-indigo-600" />
                  We typically respond within one business day.
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer data-testid="ai-footer" className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-2xl font-extrabold tracking-tight text-slate-900">
                AAEN <span className="text-indigo-600">AI Services</span>
              </p>
              <p className="text-slate-600 text-sm mt-3">
                A company of{" "}
                <a href="https://aaen.ai" className="text-indigo-600 hover:text-indigo-700 transition-colors">AAEN Holdings</a>.
              </p>
            </div>
            <p className="text-xs text-slate-400">© 2026 AAEN AI Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
